// src/pages/orders/SalesOrderApproval.jsx
import { useEffect, useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Grid,
  Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MantisDataTable from "../../../components/common/MantisDataTable";
import { useAuth } from "../../../sections/auth/AuthContext";
import { fetchState } from "../../../api/locationApi";
import { fetchorders, upsertOrders, fetchActiveProducts, fetchOrderDetailsByStatus, updateDispatchedQty     } from "../../../api/salesOrderApi";

function calculateLineTotal(rate, qty, gstPercent) {
  const gstFraction = gstPercent / 100;

  const subtotal = rate * qty;
  const gst = subtotal * gstFraction;
  const total = subtotal + gst;

  return Math.round((total + Number.EPSILON) * 100) / 100;
}

const getCurrentMonthDates = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const format = (d) => d.toISOString().split("T")[0];

  return {
    start: format(first),
    end: format(last),
  };
};

const OrderStatus = {
  DISPATCHED: "DISPATCHED",
  IN_TRANSIT: "IN_TRANSIT",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
  REJECTED: "REJECTED",
};

const SalesOrderApproval = () => {
  const [orderData, setOrderData] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCompletedView, setIsCompletedView] = useState(false);
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [completedState, setCompletedState] = useState("");
  const [completedStartDate, setCompletedStartDate] = useState("");
  const [completedEndDate, setCompletedEndDate] = useState("");
  const { userInfo } = useAuth();
  const [productMaster, setProductMaster] = useState([]);
  const [statusTimeline, setStatusTimeline] = useState({});
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);


const Info = ({ label, value, full = false }) => (
  <Box sx={{ gridColumn: full ? "1 / -1" : "auto" }}>
    <div style={{ fontWeight: 600, fontSize: "12px", color: "#555" }}>
      {label}
    </div>
    <div style={{ fontSize: "13px" }}>
      {value || "-"}
    </div>
  </Box>
);

const getAllowedNextStatuses = (currentStatus) => {
  const status = currentStatus?.toUpperCase();

  switch (status) {
    case "SUBMITTED":
      return [
        "APPROVED",
        "REJECTED",
        "CANCELLED",
      ];

    case "APPROVED":
      return [
        "DISPATCHED",
        "CANCELLED",
      ];

    case "DISPATCHED":
      return [
        "IN_TRANSIT",
      ];

    case "IN_TRANSIT":
      return [
        "DELIVERED",
      ];

    default:
      return []; // DELIVERED, REJECTED, CANCELLED → no options
  }
};

// 🔹 Format date-time nicely for timeline
const formatDateTime = (dt) => {
  if (!dt) return "";

  // Treat backend time as UTC
  const utcDate = new Date(dt + "Z");

  return utcDate.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

  const loadOrders = async (state = "", start = "", end = "") => {
    try {
      const data = await fetchorders(state, "", start, end);
      const excludedStatuses = ["DELIVERED", "CANCELLED", "REJECTED", "SUBMITTED"];
      const filtered = (data || []).filter(
        (item) => !excludedStatuses.includes(item.status?.toUpperCase())
      );
      const formattedData = filtered.map((item) => ({
        ...item,
        order_date: item.order_date ? item.order_date.split("T")[0] : "",
        distributor_state: item.distributor_state?.toLowerCase() || "unknown",
      }));
      setOrderData(formattedData);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      toast.error("Failed to fetch orders");
    }
  };

  // ✅ Fetch completed (DELIVERED) orders
  const loadCompletedOrders = async (state = "", start = "", end = "") => {
    try {
      const data = await fetchorders(state, "", start, end);
      const delivered = (data || []).filter(
        (item) => item.status?.toUpperCase() === "DELIVERED"
      );
      const formatted = delivered.map((item) => ({
        ...item,
        order_date: item.order_date ? item.order_date.split("T")[0] : "",
        distributor_state: item.distributor_state?.toLowerCase() || "unknown",
      }));
      setCompletedOrders(formatted);
    } catch (err) {
      console.error("Failed to fetch completed orders:", err);
      toast.error("Failed to fetch completed orders");
    }
  };

  const loadStates = async () => {
    try {
      const res = await fetchState();
      setStates(res || []);
    } catch (err) {
      console.error("Error fetching states:", err);
    }
  };

  useEffect(() => {
    loadStates();
  }, []);

useEffect(() => {
  const loadProducts = async () => {
    try {
      const data = await fetchActiveProducts();
      setProductMaster(data || []);
    } catch (err) {
      console.error("Error fetching product master:", err);
    }
  };
  loadProducts();
}, []);

useEffect(() => {
  const { start, end } = getCurrentMonthDates();

  setStartDate(start);
  setEndDate(end);

  loadOrders("", start, end);
  loadCompletedOrders("", start, end);
}, []);

useEffect(() => {
  if (!selectedOrder || selectedOrder.isEditing) return;

  const enrichedProducts = selectedOrder.product_list?.map((p) => {
    const productInfo = productMaster.find(
      (prod) => prod.product_code === p.product_code
    );

    if (!productInfo) return p;

    const variantInfo = productInfo.variants?.find(
      (v) => v.variant_code === p.variant_code
    );

    const rate = Number(variantInfo.variant_rate || 0);
    const gstPercent = Number(productInfo.gst_percent || 0);
    const qty = Number(p.quantity_ordered || 0);

    const subtotal = rate * qty;
    const gst = subtotal * (gstPercent / 100);
    const total = subtotal + gst;

    return {
      ...p,

      // ✅ ADD THESE
      hsn_code: productInfo.hsn_code,
      sku: variantInfo?.sku || "-",
      uom: variantInfo?.variant_uom || "-",

      rate: rate.toFixed(2),
      gst_percent: gstPercent,
      gst_amount: gst.toFixed(2),
      product_total: total.toFixed(2),
    };
  });

  setSelectedOrder((prev) => ({
    ...prev,
    product_list: enrichedProducts,
  }));
}, [selectedOrder?.order_id, productMaster]);



useEffect(() => {
  if (!selectedOrder?.order_id) return;

  const loadTimeline = async () => {
    const statuses = ["APPROVED", "DISPATCHED", "IN_TRANSIT", "DELIVERED"];
    const timelineMap = {};

    for (const st of statuses) {
      const res = await fetchOrderDetailsByStatus(
        selectedOrder.order_id,
        st
      );

      if (res?.changed_at) {
        timelineMap[st] = {
          changed_at: res.changed_at,
          changed_by: res.changed_by,
          changed_note: res.changed_note,
        };
      }
    }

    setStatusTimeline(timelineMap);
  };

  loadTimeline();
}, [selectedOrder]);


  const handleEditClick = (row) => {
    setSelectedOrder(row);
    setRemark("");
    setStatus("");
    setIsCompletedView(false);
  };

  const handleViewClick = (row) => {
    setSelectedOrder(row);
    setIsCompletedView(true);
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
    setRemark("");
    setStatus("");
    setIsCompletedView(false);
  };

  // helper to build dispatched qty payload
const buildDispatchedQtyPayload = () => {
  return (selectedOrder?.product_list || []).map((p) => ({
    product_code: p.product_code,
    variant_code: p.variant_code,
    dispatched_qty: Number(p.quantity_ordered || 0),
  }));
};


const handleUpdateOrder = async () => {
  if (!status.trim()) return toast.error("Status is required!");
  if (!remark.trim()) return toast.error("Remark is required!");

  const wordCount = remark.trim().split(/\s+/).length;
  if (wordCount > 100) {
    return toast.error("Remark must not exceed 100 words");
  }

  if (selectedOrder.isEditing || hasUnsavedEdits) {
    return toast.warning(
      "Please save or cancel the changes in order details before updating status"
    );
  }

  try {
    const statusPayload = {
      order_id: selectedOrder.order_id,
      new_status: status,
      changed_by: userInfo?.empCode || "UNKNOWN",
      note: remark,
    };

    const statusRes = await upsertOrders(statusPayload);

    const statusSuccess =
      statusRes?.success === true ||
      statusRes?.apiErrorCode === 0;

    if (!statusSuccess) {
      toast.error("Failed to update order status");
      return;
    }
if (status === "DISPATCHED") {
  const itemsPayload = buildDispatchedQtyPayload();

  if (!itemsPayload.length) {
    toast.error("No items found to dispatch");
    return;
  }

  await updateDispatchedQty(
    selectedOrder.order_id,
    itemsPayload
  );
}

    toast.success("Order updated successfully!");
    handleBackToList();
    loadOrders(selectedState, startDate, endDate);

  } catch (err) {
    console.error("Error updating order:", err);
    toast.error("Error updating order");
  }
};


  const filteredOrders = selectedState
    ? orderData.filter((o) => o.distributor_state === selectedState.toLowerCase())
    : orderData;

  const filteredCompleted = completedState
    ? completedOrders.filter((o) => o.distributor_state === completedState.toLowerCase())
    : completedOrders;
    const allowedStatuses = selectedOrder
  ? getAllowedNextStatuses(selectedOrder.status)
  : [];

const showDispatchedQty =
  selectedOrder &&
  ["DISPATCHED","IN_TRANSIT", "DELIVERED"].includes(
    selectedOrder.status?.toUpperCase()
  );


  return (
    <>
      {!selectedOrder ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)}>
              <Tab label="Orders in Process" />
              <Tab label="Completed Orders" />
            </Tabs>
          </Box>

          {/* ✅ Orders in Process */}
          {tabIndex === 0 && (
            <MantisDataTable
              title="Update Sales Order"
              data={filteredOrders}
              columns={[
                {
                  label: "Action",
                  type: "actions",
                  actions: [
  {
    type: "edit",
    label: "Edit",
    action: handleEditClick,
    condition: (row) => row.status?.toUpperCase() === "APPROVED",
  },
],

                },
                { key: "order_id", label: "Order ID" },
                { key: "customer_name", label: "Customer Name" },
                { key: "placed_by_name", label: "Placed By" },
                { key: "order_total", label: "Total (₹)" },
                { key: "approver_name", label: "Approved By" },
                { key: "status", label: "Status", type: "badge" },
                { key: "order_date", label: "Order Date" },
              ]}
              filters={[
                {
                  label: "State",
                  name: "state",
                  type: "select",
                  options: [
                    { label: "All States", value: "" },
                    ...states.map((s) => ({
                      label: s.description,
                      value: s.description,
                    })),
                  ],
                  defaultValue: selectedState,
                },
                { label: "Start Date", name: "start_date", type: "date" },
                { label: "End Date", name: "end_date", type: "date" },
              ]}
              onFilterChange={(form) => {
                const { state, start_date, end_date } = form;
                setSelectedState(state || "");
                setStartDate(start_date || "");
                setEndDate(end_date || "");
                if (start_date && end_date) loadOrders(state, start_date, end_date);
                else setOrderData([]);
              }}
            />
          )}

          {/* ✅ Completed Orders */}
          {tabIndex === 1 && (
            <MantisDataTable
              title="Delivered Orders Details"
              data={filteredCompleted}
              columns={[
                {
                  label: "Action",
                  type: "actions",
                  actions: [{ type: "view", label: "View", action: handleViewClick }],
                },
                { key: "order_id", label: "Order ID" },
                { key: "customer_name", label: "Customer Name" },
                { key: "placed_by_name", label: "Placed By" },
                { key: "order_total", label: "Total (₹)" },
                { key: "approver_name", label: "Approved By" },
                { key: "status", label: "Status", type: "badge" },
                { key: "order_date", label: "Order Date" },
              ]}
              filters={[
                {
                  label: "State",
                  name: "state",
                  type: "select",
                  options: [
                    { label: "All States", value: "" },
                    ...states.map((s) => ({
                      label: s.description,
                      value: s.description,
                    })),
                  ],
                  defaultValue: completedState,
                },
                { label: "Start Date", name: "start_date", type: "date" },
                { label: "End Date", name: "end_date", type: "date" },
              ]}
              onFilterChange={(form) => {
                const { state, start_date, end_date } = form;
                setCompletedState(state || "");
                setCompletedStartDate(start_date || "");
                setCompletedEndDate(end_date || "");
                if (start_date && end_date) loadCompletedOrders(state, start_date, end_date);
                else setCompletedOrders([]);
              }}
            />
          )}
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleBackToList}
            sx={{ mb: 2, textTransform: "none" }}
          >
            ← Back to Orders
          </Button>

          <Paper elevation={3} sx={{ p: 3, borderRadius: "12px", backgroundColor: "#fff" }}>
            <h3>Order Details (#{selectedOrder.order_id})</h3>

{/* ✅ Order Info — Clean, Readable Layout */}
<Box
  sx={{
    backgroundColor: "#f9f9f9",
    p: 2,
    borderRadius: "8px",
    fontSize: "13px",
  }}
>
  {/* Row 1 */}
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
      gap: 2,
      mb: 1.5,
    }}
  >
    <Info label="Purchase Order No :" value={selectedOrder.order_id} />
    <Info label="Order Date :" value={selectedOrder.order_date} />
    <Info label="Customer Name :" value={selectedOrder.customer_name} />
  </Box>

  {/* Row 2 */}
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
      gap: 2,
      mb: 1.5,
    }}
  >
    <Info label="Placed By :" value={selectedOrder.placed_by_name} />
    <Info label="GSTIN :" value={selectedOrder.distributor_gst_no} />
    <Info label="PAN No :" value={selectedOrder.distributor_pan_no} />
  </Box>

  {/* Row 3 — Addresses */}
   <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
      gap: 2,
      mb: 1.5,
    }}
  >
     <Info label="Billing Address :" value={selectedOrder.distributor_address} />
    <Info label="Shipping Address :" value={selectedOrder.distributor_address} />
  </Box>
</Box>

<Box sx={{ mt: 3, mb: 3 }}>
  <h4>Order Progress</h4>
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
       alignItems: "flex-start",
      mt: 2,
      position: "relative",
    }}
  >
    {[
      { label: "APPROVED", icon: "✅" },
      { label: "DISPATCHED", icon: "📦" },
      { label: "IN_TRANSIT", icon: "🚚" },
      { label: "DELIVERED", icon: "🏠" },
    ].map((step, index) => {
      const currentStatus = isCompletedView
        ? "DELIVERED"
        : selectedOrder.status?.toUpperCase() || "APPROVED";
      const steps = ["APPROVED", "DISPATCHED", "IN_TRANSIT", "DELIVERED"];
      const currentIndex = steps.indexOf(currentStatus);
      const isCompleted = index <= currentIndex;

      return (
       <Box
  key={step.label}
  sx={{
    flex: 1,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  }}
>

<Box
  sx={{
    height: 70,             
    width: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  {index < steps.length - 1 && (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        right: "-50%",
        height: "3px",
        backgroundColor: index < currentIndex ? "#1976d2" : "#ccc",
        transform: "translateY(-50%)",
        zIndex: 0,
      }}
    />
  )}

  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: "50%",
      backgroundColor: isCompleted ? "#1976d2" : "#e0e0e0",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "16px",
      zIndex: 1,
    }}
  >
    {step.icon}
  </Box>
</Box>


         <Box
  sx={{
    mt: 1,
    width: "100%",
    textAlign: "center",
  }}
>
  <div
    style={{
      fontSize: "13px",
      fontWeight: 600,
      color: isCompleted ? "#1976d2" : "#666",
    }}
  >
    {step.label.replace("_", " ")}
  </div>

  {statusTimeline[step.label] && (
    <div
      style={{
        fontSize: "11px",
        color: "#555",
        marginTop: "4px",
      }}
    >
      <div>{formatDateTime(statusTimeline[step.label].changed_at)}</div>

      <div>
        By <strong>{statusTimeline[step.label].changed_by}</strong>
      </div>

      {statusTimeline[step.label].changed_note && (
        <div style={{ fontStyle: "italic" }}>
          “{statusTimeline[step.label].changed_note}”
        </div>
      )}
    </div>
  )}
</Box>
        </Box>
      );
    })}
  </Box>
</Box>


<h4 style={{ marginTop: "10px" }}>Ordered Products</h4>

<Box
  sx={{
    mt: 1,
    overflowX: "auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
  <Box>
    {!isCompletedView &&
      selectedOrder.status?.toUpperCase() === "APPROVED" && (
        <>
          {!selectedOrder.isEditing && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mb: 1, textTransform: "none" }}
              onClick={() => {
                const initializedProducts = selectedOrder.product_list.map((p) => {
                  const productInfo = productMaster.find(
                    (prod) => prod.product_code === p.product_code
                  );

                  if (!productInfo) return p;

                  const rate = Number(variantInfo.variant_rate || 0);
                  const gstPercent = Number(productInfo.gst_percent || 0);
                  const qty = Number(p.quantity_ordered || 0);

                  const subtotal = rate * qty;
                  const gst = subtotal * (gstPercent / 100);
                  const total = subtotal + gst;

                 return {
  ...p,

  hsn_code: p.hsn_code,
  sku: p.sku,
  uom: p.uom,

  rate: rate.toFixed(2),
  gst_percent: gstPercent,
  product_subtotal: subtotal.toFixed(2),
  gst_amount: gst.toFixed(2),
  product_total: total.toFixed(2),
};

                });

                setSelectedOrder({
                  ...selectedOrder,
                  isEditing: true,
                  backupProducts: [...selectedOrder.product_list],
                  product_list: initializedProducts,
                });
              }}
            >
              ✏️ Edit
            </Button>
          )}

          {selectedOrder.isEditing && (
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{ mr: 1, mb: 1, textTransform: "none" }}
                onClick={() => {
                  setHasUnsavedEdits(false);
                  setSelectedOrder({
                    ...selectedOrder,
                    isEditing: false,
                  });
                  toast.success("Quantities updated successfully!");
                }}
              >
                💾 Save
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ mb: 1, textTransform: "none" }}
                onClick={() => {
                  setHasUnsavedEdits(false);
                  setSelectedOrder({
                    ...selectedOrder,
                    isEditing: false,
                    product_list:
                      selectedOrder.backupProducts || selectedOrder.product_list,
                  });
                }}
              >
                ❌ Cancel
              </Button>
            </>
          )}
        </>
      )}
  </Box>
</Box>

<Box sx={{ overflowX: "auto" }}>
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr style={{ backgroundColor: "#f5f5f5" }}>
        <th style={{ border: "1px solid #ddd", padding: "6px" }}>HSN</th>
      <th style={{ border: "1px solid #ddd", padding: "6px" }}>Product Code</th>
<th style={{ border: "1px solid #ddd", padding: "6px" }}>Product Name</th>
<th style={{ border: "1px solid #ddd", padding: "6px" }}>SKU</th>
<th style={{ border: "1px solid #ddd", padding: "6px" }}>UOM</th>
<th style={{ border: "1px solid #ddd", padding: "6px" }}>Qty</th>


        {showDispatchedQty && (
          <th style={{ border: "1px solid #ddd", padding: "6px" }}>
            Dispatched Qty
          </th>
        )}

        <th style={{ border: "1px solid #ddd", padding: "6px" }}>Rate (₹)</th>
        <th style={{ border: "1px solid #ddd", padding: "6px" }}>GST %</th>
        <th style={{ border: "1px solid #ddd", padding: "6px" }}>
          GST Amt (₹)
        </th>

        <th
          style={{
            border: "1px solid #ddd",
            padding: "6px",
            textAlign: "right",
          }}
        >
          Total (₹)
        </th>
      </tr>
    </thead>

    <tbody>
      {selectedOrder.product_list?.map((p, i) => {
        const originalQty =
          selectedOrder.backupProducts?.[i]?.quantity_ordered ??
          p.quantity_ordered;

        const handleQtyChange = (e) => {
          let newQty = Number(e.target.value) || 0;

          if (newQty < 0 || newQty > originalQty) return;

          setHasUnsavedEdits(true);

          const productInfo = productMaster.find(
            (prod) => prod.product_code === p.product_code
          );

          if (!productInfo) return;

          const rate = Number(productInfo.variant_rate || 0);
          const gstPercent = Number(productInfo.gst_percent || 0);

          const subtotal = rate * newQty;
          const gst = subtotal * (gstPercent / 100);
          const total = subtotal + gst;

          const updatedList = selectedOrder.product_list.map((item, idx) =>
           idx === i
  ? {
      ...item,

      hsn_code: item.hsn_code,
      sku: item.sku,
      uom: item.uom,

      quantity_ordered: newQty,
      rate: rate.toFixed(2),
      gst_percent: gstPercent,
      gst_amount: gst.toFixed(2),
      product_total: total.toFixed(2),
    }
  : item

          );

          setSelectedOrder({ ...selectedOrder, product_list: updatedList });
        };

        return (
          <tr key={i}>
            <td style={{ border: "1px solid #ddd", padding: "6px" }}>
  {p.hsn_code}
</td>
         <td style={{ border: "1px solid #ddd", padding: "6px" }}>
  {p.product_code}
</td>
<td style={{ border: "1px solid #ddd", padding: "6px" }}>
  {p.product_name}
</td>
<td style={{ border: "1px solid #ddd", padding: "6px" }}>
  {p.sku}
</td>
<td style={{ border: "1px solid #ddd", padding: "6px" }}>
  {p.uom}
</td>


            <td style={{ border: "1px solid #ddd", padding: "6px" }}>
              {selectedOrder.isEditing ? (
                <input
                  type="number"
                  min="0"
                  max={originalQty}
                  value={p.quantity_ordered}
                  onChange={handleQtyChange}
                  style={{ width: "70px" }}
                />
              ) : (
                p.quantity_ordered
              )}
            </td>

            {showDispatchedQty && (
              <td
                style={{
                  border: "1px solid #ddd",
                  padding: "6px",
                  textAlign: "center",
                }}
              >
                {p.dispatched_qty ?? "-"}
              </td>
            )}

            <td style={{ border: "1px solid #ddd", padding: "6px" }}>
              ₹{Number(p.rate || 0).toFixed(2)}
            </td>
            <td style={{ border: "1px solid #ddd", padding: "6px" }}>
              {Number(p.gst_percent || 0)}%
            </td>
            <td style={{ border: "1px solid #ddd", padding: "6px" }}>
              ₹{Number(p.gst_amount || 0).toFixed(2)}
            </td>

            <td
              style={{
                border: "1px solid #ddd",
                padding: "6px",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              ₹{Number(p.product_total || 0).toFixed(2)}
            </td>
          </tr>
        );
      })}
    </tbody>

    <tfoot>
      <tr>
        <td
         colSpan={showDispatchedQty ? 10 : 9}
          style={{ border: "none" }}
        ></td>
        <td
          style={{
            borderTop: "2px solid #000",
            padding: "8px 12px",
            textAlign: "right",
            fontWeight: "bold",
            backgroundColor: "#f9f9f9",
          }}
        >
          Total Amount:{" "}
          <span style={{ color: "#1976d2" }}>
            ₹
            {selectedOrder.product_list
              ?.reduce((sum, p) => sum + Number(p.product_total || 0), 0)
              .toFixed(2)}
          </span>
        </td>
      </tr>
    </tfoot>
  </table>
</Box>


{!isCompletedView && (
  <>
    <h4 style={{ marginTop: "10px" }}>Update Order Status</h4>

    <Box sx={{ mt: 3 }}>
      {allowedStatuses.length > 0 && (
        <TextField
          select
          fullWidth
          label="Select Status *"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          margin="dense"
        >
          {allowedStatuses.map((st) => (
            <MenuItem key={st} value={st}>
              {st.replace("_", " ")}
            </MenuItem>
          ))}
        </TextField>
      )}

     <TextField
  fullWidth
  label="Remark *"
  value={remark}
  onChange={(e) => {
    const value = e.target.value;
    const words = value.trim().split(/\s+/);

    if (words.length <= 100) {
      setRemark(value);
    }
  }}
  margin="dense"
  multiline
  rows={3}
  placeholder="Enter remark (max 100 words)"
  helperText={`${remark.trim() ? remark.trim().split(/\s+/).length : 0}/100 words`}
/>


      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={handleUpdateOrder}
        disabled={allowedStatuses.length === 0}
      >
        Update Order
      </Button>
    </Box>
  </>
)}
          </Paper>
        </Box>
      )}
    </>
  );
};

export default SalesOrderApproval;


